import { styled } from '@mui/material';

import { useEffect } from 'react';

import { FieldErrors, FormProvider, useForm } from 'react-hook-form';

import { useI18n } from '@app-velocity/i18n';
import {
  Button,
  ButtonsContainer,
  ErrorMessage,
  Form,
  Modal,
  useModalMenuContext,
  useModalWrapperContext,
} from '@app-velocity/ui';

import { usePersonaModalParams } from '../../../../hooks/usePersonaModalParams';
import {
  useCreatePersonaMutation,
  useGetPersonaQuery,
  useUpdatePersonaMutation,
} from '../../../../query';

import { getTabForField } from './utils/getTabForField';
import {
  buildPersonaConfig,
  getPersonaFormDefaultValues,
} from './utils/config';
import { PersonaFormValues } from './types/persona';
import { PersonaSections } from './components/PersonaSections';

const StyledFormContent = styled('div')({
  width: '100%',
  minWidth: 520,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

export const PersonaForm = () => {
  const { teamId, personaId } = usePersonaModalParams();
  const { data: persona, isLoading } = useGetPersonaQuery(teamId, personaId);
  const { t } = useI18n();
  const { closeModal } = useModalWrapperContext();
  const { setSelectedMenuKey } = useModalMenuContext();

  const {
    mutate: createPersona,
    isPending: isCreating,
    error: createError,
    reset: resetCreate,
    isSuccess: isCreateSuccess,
  } = useCreatePersonaMutation(teamId!);

  const {
    mutate: updatePersona,
    isPending: isUpdating,
    error: updateError,
    reset: resetUpdate,
    isSuccess: isUpdateSuccess,
  } = useUpdatePersonaMutation(teamId!);

  const methods = useForm<PersonaFormValues>({
    defaultValues: getPersonaFormDefaultValues(persona),
    mode: 'onSubmit',
    shouldUnregister: false,
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(getPersonaFormDefaultValues(persona));
    resetCreate();
    resetUpdate();
  }, [persona, reset, resetCreate, resetUpdate]);

  useEffect(() => {
    if (isCreateSuccess || isUpdateSuccess) {
      closeModal();
    }
  }, [isCreateSuccess, isUpdateSuccess, closeModal]);

  const onInvalid = (errors: FieldErrors<PersonaFormValues>) => {
    const firstErrorField = Object.keys(errors)[0];

    if (!firstErrorField) return;

    setSelectedMenuKey(getTabForField(firstErrorField));
  };

  const onSubmit = (values: PersonaFormValues) => {
    const body = {
      name: values.name.trim(),
      config: buildPersonaConfig(values),
    };

    if (persona?.id) {
      updatePersona({ personaId: persona.id, body });

      return;
    }

    createPersona(body);
  };

  if (!teamId) return null;

  if (personaId && !isLoading && !persona) return null;

  return (
    <StyledFormContent>
      <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        {/* div to avoid Form gap */}
        <div>
          <FormProvider {...methods}>
            <Modal.Header
              title={
                persona
                  ? 'personas:form.edit_title'
                  : 'personas:form.create_title'
              }
            />
            <Modal.Content>
              <PersonaSections isLoading={isLoading} />
            </Modal.Content>
            <Modal.Controls>
              <ButtonsContainer>
                <Button
                  variant="contained"
                  color="secondary"
                  type="button"
                  onClick={() => closeModal()}
                >
                  {t('shared:cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isCreating || isUpdating || isLoading}
                >
                  {t('shared:save')}
                </Button>
              </ButtonsContainer>
            </Modal.Controls>
          </FormProvider>
        </div>
      </Form>

      <ErrorMessage
        error={createError || updateError}
        statusMap={{
          400: t('personas:form.unable_to_save'),
        }}
      />
    </StyledFormContent>
  );
};
